import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const FACILITY_OPEN_SHIFT_COUNT_ENDPOINT_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/legacy-api/facilityOpenShiftCount`;

const workplaceWithShiftCountQuerySchema = z.object({
  range: z.object({
    start: z.string(),
    end: z.string(),
  }),
  coordinates: z.array(z.number()),
  distance: z.number(),
  qualification: z.string(),
  specialities: z
    .object({
      experience: z.array(z.string()).optional(),
      hasSedationExperience: z.boolean().optional(),
      hasTrayAssemblyExperience: z.boolean().optional(),
    })
    .optional(),
  isPlain: z.boolean().optional(),
  facilityId: z.string().optional(),
  isAgent: z.literal(true),
});

export type WorkplaceWithShiftCountParams = z.infer<typeof workplaceWithShiftCountQuerySchema>;

export const facilityWithOpenShiftsSchema = z.object({
  _id: z.string(),
  facilityIdString: z.string(),
  shiftsCount: z.number(),
});

export const facilityOpenShiftsCountResponse = z.array(facilityWithOpenShiftsSchema);
export type FacilityOpenShiftsCountResponse = z.infer<typeof facilityOpenShiftsCountResponse>;

export function useWorkplaceWithShiftCount(
  params: WorkplaceWithShiftCountParams,
  options: UseGetQueryOptions<FacilityOpenShiftsCountResponse>
): UseQueryResult<FacilityOpenShiftsCountResponse> {
  return useGetQuery<WorkplaceWithShiftCountParams, FacilityOpenShiftsCountResponse>({
    url: FACILITY_OPEN_SHIFT_COUNT_ENDPOINT_URL,
    requestSchema: workplaceWithShiftCountQuerySchema,
    responseSchema: facilityOpenShiftsCountResponse,
    queryParams: params,
    meta: {
      userErrorMessage: "Something went wrong while loading workplace shift count",
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACE_OPEN_SHIFT_COUNT_FAILURE,
    },
    ...options,
  });
}
